// =============================
// File: src/components/admin/DocEditor.jsx
// =============================
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import axios from 'axios';
import DocContentBuilder from './DocContentBuilder';

const API_URL = process.env.REACT_APP_API_URL;

function DocEditor({ doc, categories, onBack, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    short_description: '',
    category_id: '',
    role_access: 'all',
    is_featured: false,
    featured_order: null
  });
  const [contentBlocks, setContentBlocks] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [docId, setDocId] = useState(null);

  useEffect(() => {
    if (doc) {
      setFormData({
        title: doc.title,
        slug: doc.slug,
        short_description: doc.short_description,
        category_id: doc.category_id,
        role_access: doc.role_access,
        is_featured: doc.is_featured,
        featured_order: doc.featured_order
      });
      setContentBlocks(doc.content_blocks || []);
      setDocId(doc.id);
    }
  }, [doc]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!doc && formData.title) {
      const slug = formData.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, doc]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }

  async function handleSave() {
    // Validation
    if (!formData.title.trim()) {
      alert('El título es obligatorio');
      return;
    }
    if (!formData.slug.trim()) {
      alert('El slug es obligatorio');
      return;
    }
    if (!formData.short_description.trim()) {
      alert('La descripción corta es obligatoria');
      return;
    }
    if (!formData.category_id) {
      alert('Debes seleccionar una categoría');
      return;
    }

    try {
      setIsSaving(true);

      let currentDocId = docId;

      // Step 1: Create/Update document metadata
      if (docId) {
        // Update existing doc
        await axios.put(`${API_URL}/api/docs/${docId}`, formData);
      } else {
        // Create new doc
        const response = await axios.post(`${API_URL}/api/docs`, formData);
        currentDocId = response.data.doc_id;
        setDocId(currentDocId);
      }

      // Step 2: Delete all existing content blocks (if editing)
      if (doc && doc.content_blocks) {
        await Promise.all(
          doc.content_blocks.map(block =>
            axios.delete(`${API_URL}/api/docs/blocks/${block.id}`)
          )
        );
      }

      // Step 3: Create new content blocks
      await Promise.all(
        contentBlocks.map((block, index) =>
          axios.post(`${API_URL}/api/docs/${currentDocId}/blocks`, {
            ...block,
            order_index: index
          })
        )
      );

      alert('Documento guardado exitosamente');
      onSave();
    } catch (error) {
      console.error('Error saving doc:', error);
      alert('Error al guardar el documento');
    } finally {
      setIsSaving(false);
    }
  }

  function handlePreview() {
    if (docId) {
      window.open(`/#/user-docs/${formData.slug}`, '_blank');
    } else {
      alert('Primero guarda el documento para ver la vista previa');
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-pink-600 shadow-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <h1 className="text-xl sm:text-2xl font-extrabold text-white font-montserrat">
                {doc ? 'Editar Documento' : 'Nuevo Documento'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handlePreview}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vista Previa</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-white text-brand-primary hover:bg-white/90 rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{isSaving ? 'Guardando...' : 'Guardar'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Metadata Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-bold text-slate-900 font-montserrat mb-4">
                Información del Documento
              </h2>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ej: Cómo hacer login"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                  />
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Slug (URL) *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="como-hacer-login"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm"
                  />
                  <p className="text-xs text-slate-500 font-montserrat mt-1">
                    URL: /user-docs/{formData.slug || '...'}
                  </p>
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Descripción Corta *
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Breve descripción para las tarjetas..."
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat text-sm resize-none"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Categoría *
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role Access */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Acceso por Rol *
                  </label>
                  <select
                    name="role_access"
                    value={formData.role_access}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                  >
                    <option value="all">Todos los roles</option>
                    <option value="customer">Solo Usuarios</option>
                    <option value="driver">Solo Conductores</option>
                    <option value="provider">Solo Proveedores</option>
                    <option value="admin">Solo Administradores</option>
                  </select>
                </div>

                {/* Featured Checkbox */}
                <div className="pt-4 border-t border-slate-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_featured"
                      checked={formData.is_featured}
                      onChange={handleChange}
                      className="w-5 h-5 text-brand-primary rounded focus:ring-2 focus:ring-brand-primary"
                    />
                    <span className="text-sm font-semibold text-slate-700 font-montserrat">
                      Destacar en banner
                    </span>
                  </label>
                  <p className="text-xs text-slate-500 font-montserrat mt-2 ml-8">
                    Los documentos destacados aparecen en el carrusel de la página principal (máx. 5)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Content Builder */}
          <div className="lg:col-span-2">
            <DocContentBuilder
              blocks={contentBlocks}
              onBlocksChange={setContentBlocks}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocEditor;